/**
 * OpenAI Realtime Voice Component
 * 
 * Real-time voice conversations using OpenAI's Realtime API via WebRTC.
 * Supports tool calling for tasks, calendar, emails, notes, and Google services.
 */

import React, { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabaseClient';

import VoiceOrb from './audio_video/VoiceOrb';
import ProgressRing from './audio_video/ProgressRing';
import AudioWaveform from './audio_video/AudioWaveform';
import { FeatureTagsContainer } from './audio_video/FeatureTag';
import './audio_video/VoiceChatEnhanced.css';

const BACKEND_URL = process.env.REACT_APP_ZOOMEY_API_URL || 'http://localhost:3000';

// Tool definitions for OpenAI Realtime
const TOOLS = [
  {
    type: 'function',
    name: 'get_current_time',
    description: 'Get the current date and time. Call this when user asks what time or date it is.',
  },
  {
    type: 'function',
    name: 'list_tasks',
    description: "List the user's tasks. Call this when the user asks about their tasks, to-do list, or pending items.",
  },
  {
    type: 'function',
    name: 'create_task',
    description: 'Create a new task for the user',
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'The task title or description' },
        category: { type: 'string', description: 'Category name (e.g., Work, Personal)' },
      },
      required: ['title'],
    },
  },
  {
    type: 'function',
    name: 'list_notes',
    description: "List the user's notes",
  },
  {
    type: 'function',
    name: 'create_note',
    description: 'Create a new note',
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Note title' },
        content: { type: 'string', description: 'Note content' },
      },
      required: ['title'],
    },
  },
  {
    type: 'function',
    name: 'list_reminders',
    description: "List the user's reminders",
  },
  {
    type: 'function',
    name: 'create_reminder',
    description: 'Create a new reminder',
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Reminder title' },
        reminder_time: { type: 'string', description: "When to remind (e.g., 'in 30 minutes', 'tomorrow at 9am')" },
      },
      required: ['title', 'reminder_time'],
    },
  },
  {
    type: 'function',
    name: 'google_calendar_list_events',
    description: 'List upcoming events from Google Calendar. Use when user asks about their schedule, meetings, or calendar.',
    parameters: {
      type: 'object',
      properties: {
        time_range: {
          type: 'string',
          description: 'Time range: today, tomorrow, this_week, next_week',
          enum: ['today', 'tomorrow', 'this_week', 'next_week'],
        },
      },
    },
  },
  {
    type: 'function',
    name: 'google_calendar_create_event',
    description: 'Create a new calendar event',
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Event title' },
        start_time: { type: 'string', description: "Start time (e.g., 'tomorrow at 3pm')" },
        end_time: { type: 'string', description: 'End time' },
        description: { type: 'string', description: 'Event description' },
      },
      required: ['title', 'start_time'],
    },
  },
  {
    type: 'function',
    name: 'gmail_list_emails',
    description: 'List recent emails from Gmail inbox',
    parameters: {
      type: 'object',
      properties: {
        max_results: { type: 'string', description: 'Maximum number of emails (default: 10)' },
        query: { type: 'string', description: 'Search query to filter emails' },
      },
    },
  },
  {
    type: 'function',
    name: 'gmail_send_email',
    description: 'Send an email via Gmail',
    parameters: {
      type: 'object',
      properties: {
        to: { type: 'string', description: 'Recipient email address' },
        subject: { type: 'string', description: 'Email subject' },
        body: { type: 'string', description: 'Email body content' },
      },
      required: ['to', 'subject', 'body'],
    },
  },
  {
    type: 'function',
    name: 'search_web',
    description: 'Search the web for information',
    parameters: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'Search query' },
      },
      required: ['query'],
    },
  },
  {
    type: 'function',
    name: 'list_groceries',
    description: 'List grocery items',
  },
  {
    type: 'function',
    name: 'add_grocery',
    description: 'Add an item to the grocery list',
    parameters: {
      type: 'object',
      properties: {
        item_name: { type: 'string', description: 'Item name' },
        quantity: { type: 'string', description: 'Quantity' },
      },
      required: ['item_name'],
    },
  },
  {
    type: 'function',
    name: 'navigate_app',
    description: `Navigate to different sections or open specific items in the Zoomey app.
Use this when user says "open tasks", "show my lists", "go to notes", "open reminders", "show meetings", "open settings", "open topic [name]", "open subtopic [name]", "open scratchpad", "open notifications", "open grocery list", "open queue", "open voice history", "go home", "close panels", etc.

Actions:
- open_tasks: Opens the Tasks/Checklist section (user's to-do items)
- open_lists: Opens the Lists section (custom user lists)
- open_notes: Opens the Notes section
- open_session: Opens a specific chat session, topic, or subtopic by name (requires session_name). Use for "open session X", "open topic X", "open subtopic X"
- open_scratchpad: Opens the Scratchpad section (quick notes area)
- open_notification: Opens the Notifications section
- open_reminders: Opens the Reminders section
- open_meetings: Opens the Meetings section (Fireflies meeting transcripts)
- open_settings: Opens the Settings/Profile page
- open_grocery: Opens the Grocery List section
- open_queue: Opens the Queue section (pending items)
- open_voice_history: Opens the Voice Chat History section
- go_home: Returns to home/main chat view
- close_panels: Closes all side panels`,
    parameters: {
      type: 'object',
      properties: {
        action: {
          type: 'string',
          description: 'Navigation action to perform',
          enum: [
            'open_tasks',
            'open_lists',
            'open_notes',
            'open_session',
            'open_scratchpad',
            'open_notification',
            'open_reminders',
            'open_meetings',
            'open_settings',
            'open_grocery',
            'open_queue',
            'open_voice_history',
            'go_home',
            'close_panels'
          ],
        },
        session_name: { type: 'string', description: 'Session, topic, or subtopic name to search and open (for open_session action)' },
        scratchpad_title: { type: 'string', description: 'Scratchpad title to search and open (for open_scratchpad action)' },
        meeting_id: { type: 'string', description: 'Meeting ID/UUID to open (for open_meetings action)' },
      },
      required: ['action'],
    },
  },
  {
    type: 'function',
    name: 'manage_scratchpad',
    description: `Manage scratchpad notes - quick notes for temporary information.
Use when user says "create a scratchpad", "show my scratchpads", "add to scratchpad", "what's in my scratchpad", etc.

Actions:
- create_scratchpad: Create a new scratchpad note
- list_scratchpads: List all scratchpad notes
- get_scratchpad: Get a specific scratchpad by ID
- update_scratchpad: Update scratchpad title or content
- delete_scratchpad: Delete a scratchpad
- get_active_scratchpad: Get the currently active scratchpad`,
    parameters: {
      type: 'object',
      properties: {
        action: {
          type: 'string',
          description: 'Action to perform',
          enum: ['create_scratchpad', 'list_scratchpads', 'get_scratchpad', 'update_scratchpad', 'delete_scratchpad', 'get_active_scratchpad'],
        },
        title: { type: 'string', description: 'Scratchpad title (for create/update)' },
        content: { type: 'string', description: 'Scratchpad content/description' },
        scratchpad_id: { type: 'number', description: 'Scratchpad ID (for get/update/delete)' },
      },
      required: ['action'],
    },
  },
  {
    type: 'function',
    name: 'manage_notifications',
    description: `Manage notifications - view, search, and manage app notifications.
Use when user asks "what notifications do I have?", "show unread notifications", "mark notification as read", "any new messages?", etc.

Actions:
- list_notifications: List recent notifications
- list_unread: List only unread notifications
- get_notification: Get a specific notification
- mark_read: Mark a notification as read
- mark_all_read: Mark all notifications as read
- delete_notification: Delete a notification
- search_notifications: Search notifications by title/message`,
    parameters: {
      type: 'object',
      properties: {
        action: {
          type: 'string',
          description: 'Action to perform',
          enum: ['list_notifications', 'list_unread', 'get_notification', 'mark_read', 'mark_all_read', 'delete_notification', 'search_notifications'],
        },
        notification_id: { type: 'number', description: 'Notification ID (for get/mark_read/delete)' },
        query: { type: 'string', description: 'Search query (for search_notifications)' },
        limit: { type: 'number', description: 'Max results to return (default 10)' },
      },
      required: ['action'],
    },
  },
  {
    type: 'function',
    name: 'manage_sessions',
    description: `Manage chat sessions, topics, and subtopics. Sessions are organized hierarchically:
- Topics: Parent containers for organizing conversations (like folders)
- Subtopics: Child sessions under a topic (type="sub_topic" with parent_session_id)

Use when user says:
- "create new topic [name]", "create a topic called Work", "new topic for personal"
- "create subtopic [name] under [topic]", "add subtopic Meeting Notes to Work topic"
- "show my topics", "list all topics", "what topics do I have?" (shows both topics and subtopics)
- "open topic [name]", "switch to work topic", "go to personal topic"
- "rename topic [name] to [new_name]"

Actions:
- list_sessions: List all topics and subtopics (returns numbered list for easy selection)
- create_session: Create a new topic or subtopic (use session_type and parent_session_id for subtopics)
- open_session: Switch to/open a topic by name (triggers navigation via is_call flag)
- update_session: Rename a topic (provide name and new_name)
- delete_session: NOT ALLOWED - topics are permanent records
- close_session: NOT ALLOWED - offers switch to another topic instead`,
    parameters: {
      type: 'object',
      properties: {
        action: {
          type: 'string',
          description: 'Action to perform',
          enum: ['list_sessions', 'create_session', 'open_session', 'update_session', 'delete_session', 'close_session'],
        },
        name: { type: 'string', description: 'Topic/subtopic name (for create/open/update). Examples: "Work", "Personal", "Meeting Notes"' },
        new_name: { type: 'string', description: 'New name when renaming a topic (for update_session)' },
        session_type: {
          type: 'string',
          description: 'Type of session: "topic" for parent topic, "sub_topic" for child under a topic',
          enum: ['topic', 'sub_topic'],
        },
        parent_session_id: { type: 'string', description: 'Parent topic ID when creating a subtopic. Required when session_type is "sub_topic".' },
      },
      required: ['action'],
    },
  },
];

const OpenAIRealtimeTest = forwardRef(({
  onVoiceStart,
  onVoiceEnd,
  onVoiceTranscriptUpdate,
  onVoiceVideoChange, // (videoEnabled, isScreen) => void - notify when video/screen state changes
  showTranscript = true,  // Control transcript visibility (hidden in left sidebar)
  showUI = true  // Control UI visibility - when false, only renders minimal hidden elements
}, ref) => {
  const [status, setStatus] = useState('idle');
  const [transcript, setTranscript] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]); // Store all messages for live chat view
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true); // Always expanded, no dropdown
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isAutoCapture, setIsAutoCapture] = useState(true); // Auto-capture enabled by default
  const [isMicMuted, setIsMicMuted] = useState(false); // Microphone mute state
  const [audioLevel, setAudioLevel] = useState(0); // For audio visualization
  const [activeFeatures, setActiveFeatures] = useState([]); // Track active features during voice chat

  const peerConnectionRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const audioAnimationRef = useRef(null);
  const dataChannelRef = useRef(null);
  const audioElementRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const videoStreamRef = useRef(null);
  const videoElementRef = useRef(null);
  const canvasRef = useRef(null);
  const captureIntervalRef = useRef(null);
  const userIdRef = useRef(null);
  const tokenRef = useRef(null);
  const sessionIdRef = useRef(null); // Voice chat session ID for history
  const sessionStartTimeRef = useRef(null);
  const responseInProgressRef = useRef(false); // Track if AI is currently responding
  const currentResponseIdRef = useRef(null); // Track current response ID
  const responseStartTimeRef = useRef(null); // Track when response started for duration
  const voiceSessionIdRef = useRef(null); // Session ID for usage tracking
  const failedCallsRef = useRef(new Map()); // Track failed tool calls for retry limiting

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  // Sync conversation history with parent component
  useEffect(() => {
    onVoiceTranscriptUpdate?.(conversationHistory);
  }, [conversationHistory, onVoiceTranscriptUpdate]);

  // Notify parent when video/screen state changes
  useEffect(() => {
    onVoiceVideoChange?.(isVideoEnabled, isScreenSharing);
  }, [isVideoEnabled, isScreenSharing, onVoiceVideoChange]);

  const connect = useCallback(async () => {
    try {
      setStatus('connecting');
      setError(null);
      setTranscript('');
      setConversationHistory([]); // Clear conversation history on new connection

      // PERFORMANCE OPTIMIZATION: Request mic permission FIRST
      // This shows the permission popup immediately while backend processes memory
      console.log('[OpenAIRealtime] Requesting microphone access...');
      const micPromise = navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      // Get auth token (fast, from Supabase cache)
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }
      tokenRef.current = session.access_token;
      
      const { data: { user } } = await supabase.auth.getUser();
      userIdRef.current = user?.id;

      // Step 1: Get ephemeral token from backend AND wait for mic permission IN PARALLEL
      // This way the user sees permission popup immediately while backend fetches memory
      console.log('[OpenAIRealtime] Getting ephemeral token (parallel with mic permission)...');
      const [tokenResponse, stream] = await Promise.all([
        fetch(`${BACKEND_URL}/api/openai-realtime/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokenRef.current}`,
          },
        }),
        micPromise,
      ]);

      // Store mic stream immediately
      mediaStreamRef.current = stream;

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to get token: ${tokenResponse.status}`);
      }

      const tokenData = await tokenResponse.json();
      const ephemeralKey = tokenData.client_secret?.value;
      
      if (!ephemeralKey) {
        throw new Error('No ephemeral key received');
      }
      
      console.log('[OpenAIRealtime] Got ephemeral token, session:', tokenData.session_id);

      // Step 2: Create peer connection
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });
      peerConnectionRef.current = pc;

      // Set up audio playback
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      audioElementRef.current = audioEl;

      pc.ontrack = (e) => {
        console.log('[OpenAIRealtime] Received remote track');
        audioEl.srcObject = e.streams[0];
      };

      pc.oniceconnectionstatechange = () => {
        console.log('[OpenAIRealtime] ICE state:', pc.iceConnectionState);
        if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
          setStatus('error');
          setError('Connection lost');
        }
      };

      // Add local audio track
      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream);
      });

      // Set up audio level analysis for visualization
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        // Start audio level monitoring
        const updateAudioLevel = () => {
          if (analyserRef.current) {
            const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
            analyserRef.current.getByteFrequencyData(dataArray);
            const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
            setAudioLevel(average / 255);
          }
          audioAnimationRef.current = requestAnimationFrame(updateAudioLevel);
        };
        updateAudioLevel();
      } catch (audioErr) {
        console.warn('[OpenAIRealtime] Audio analysis not available:', audioErr);
      }

      // Create data channel for events
      const dc = pc.createDataChannel('oai-events');
      dataChannelRef.current = dc;

      dc.onopen = () => {
        console.log('[OpenAIRealtime] Data channel open - ready to chat!');
        setStatus('listening');
        // Notify parent that voice session has started
        onVoiceStart?.();
      };

      dc.onmessage = (e) => {
        handleServerEvent(JSON.parse(e.data));
      };

      dc.onerror = (e) => {
        console.error('[OpenAIRealtime] Data channel error:', e);
      };

      // Create and send offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Wait for ICE gathering
      await new Promise((resolve) => {
        if (pc.iceGatheringState === 'complete') {
          resolve();
        } else {
          pc.onicegatheringstatechange = () => {
            if (pc.iceGatheringState === 'complete') {
              resolve();
            }
          };
          setTimeout(resolve, 3000);
        }
      });

      // Step 3: Send SDP to OpenAI with ephemeral key
      console.log('[OpenAIRealtime] Sending SDP to OpenAI...');
      const sdpResponse = await fetch('https://api.openai.com/v1/realtime?model=gpt-4o-realtime-preview-2024-12-17', {
        method: 'POST',
        body: pc.localDescription.sdp,
        headers: {
          'Content-Type': 'application/sdp',
          'Authorization': `Bearer ${ephemeralKey}`,
        },
      });

      if (!sdpResponse.ok) {
        const errorText = await sdpResponse.text();
        throw new Error(`OpenAI SDP error: ${sdpResponse.status} - ${errorText}`);
      }

      const answerSdp = await sdpResponse.text();
      
      await pc.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp,
      });

      console.log('[OpenAIRealtime] WebRTC connection established!');

      // Create voice chat session for history
      sessionStartTimeRef.current = new Date();
      try {
        const sessionResponse = await fetch(`${BACKEND_URL}/api/voice-chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokenRef.current}`,
          },
          body: JSON.stringify({
            model: 'gpt-realtime',
          }),
        });
        if (sessionResponse.ok) {
          const sessionData = await sessionResponse.json();
          sessionIdRef.current = sessionData.session?.id;
          // Also use the same ID for usage tracking
          voiceSessionIdRef.current = sessionData.session?.id;
          console.log('[OpenAIRealtime] Voice chat session created:', sessionIdRef.current);
        }
      } catch (historyErr) {
        console.warn('[OpenAIRealtime] Failed to create history session:', historyErr);
      }
    } catch (err) {
      console.error('[OpenAIRealtime] Connection error:', err);
      setError(err.message || 'Connection failed');
      setStatus('error');
      disconnect();
    }
  }, []);

  // Save message to voice chat history
  const saveVoiceChatMessage = useCallback(async (role, content) => {
    if (!sessionIdRef.current || !tokenRef.current || !content) return;
    
    try {
      await fetch(`${BACKEND_URL}/api/voice-chat/${sessionIdRef.current}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenRef.current}`,
        },
        body: JSON.stringify({ role, content }),
      });
    } catch (err) {
      console.warn('[OpenAIRealtime] Failed to save message:', err);
    }
  }, []);

  // Save voice usage data for billing/tracking
  const saveVoiceUsage = useCallback(async (response, durationSeconds = 0) => {
    if (!userIdRef.current || !voiceSessionIdRef.current) return;
    if (!response?.usage) {
      console.log('[OpenAIRealtime] No usage data in response');
      return;
    }

    try {
      const usageRes = await fetch(`${BACKEND_URL}/api/voice-usage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenRef.current}`,
        },
        body: JSON.stringify({
          user_id: userIdRef.current,
          session_id: voiceSessionIdRef.current,
          response_id: response.id,
          usage: response.usage,
          model: 'gpt-4o-realtime-preview',
          duration_seconds: durationSeconds,
        }),
      });

      if (usageRes.ok) {
        const data = await usageRes.json();
        console.log('[OpenAIRealtime] Usage saved, cost:', data.cost_cents, 'cents, duration:', durationSeconds, 's');
      }
    } catch (err) {
      console.warn('[OpenAIRealtime] Failed to save usage:', err);
    }
  }, []);

  // Safely create a new response, canceling any in-progress response first
  const createResponse = useCallback(() => {
    if (!dataChannelRef.current || dataChannelRef.current.readyState !== 'open') {
      return;
    }
    
    // If there's a response in progress, cancel it first
    if (responseInProgressRef.current && currentResponseIdRef.current) {
      console.log('[OpenAIRealtime] Canceling in-progress response:', currentResponseIdRef.current);
      dataChannelRef.current.send(JSON.stringify({
        type: 'response.cancel',
      }));
      responseInProgressRef.current = false;
      currentResponseIdRef.current = null;
    }
    
    console.log('[OpenAIRealtime] Creating new response');
    dataChannelRef.current.send(JSON.stringify({
      type: 'response.create',
    }));
  }, []);

  const handleServerEvent = useCallback(async (event) => {
    const { type } = event;

    // Log important events (not audio deltas)
    if (!type.includes('audio.delta')) {
      console.log('[OpenAIRealtime] Event:', type);
    }

    switch (type) {
      case 'session.created':
        console.log('[OpenAIRealtime] Session created');
        break;

      case 'session.updated':
        console.log('[OpenAIRealtime] Session updated');
        break;

      case 'input_audio_buffer.speech_started':
        setStatus('listening');
        setTranscript('');
        // Start tracking duration from when user begins speaking
        if (!responseStartTimeRef.current) {
          responseStartTimeRef.current = Date.now();
        }
        break;

      case 'input_audio_buffer.speech_stopped':
        setStatus('thinking');
        break;

      case 'conversation.item.input_audio_transcription.completed':
        if (event.transcript) {
          console.log('[OpenAIRealtime] User said:', event.transcript);
          setTranscript(`You: ${event.transcript}`);
          // Add to conversation history for live chat view
          setConversationHistory(prev => [...prev, { role: 'user', content: event.transcript, timestamp: Date.now() }]);
          // Save user message to history
          saveVoiceChatMessage('user', event.transcript);
        }
        break;

      case 'response.created':
        // Track that a response has started
        responseInProgressRef.current = true;
        currentResponseIdRef.current = event.response?.id;
        // Only set start time if not already set by speech_started
        if (!responseStartTimeRef.current) {
          responseStartTimeRef.current = Date.now();
        }
        console.log('[OpenAIRealtime] Response started:', currentResponseIdRef.current);
        break;

      case 'response.audio_transcript.delta':
        setStatus('speaking');
        break;

      case 'response.audio_transcript.done':
        if (event.transcript) {
          setTranscript(`Zoomey: ${event.transcript}`);
          // Add to conversation history for live chat view
          setConversationHistory(prev => [...prev, { role: 'assistant', content: event.transcript, timestamp: Date.now() }]);
          // Save assistant response to history
          saveVoiceChatMessage('assistant', event.transcript);
        }
        break;

      case 'response.function_call_arguments.done':
        setStatus('thinking');
        await handleFunctionCall(event);
        break;

      case 'response.done':
        // Mark response as complete
        responseInProgressRef.current = false;
        currentResponseIdRef.current = null;

        // Calculate response duration
        const durationSeconds = responseStartTimeRef.current
          ? Math.round((Date.now() - responseStartTimeRef.current) / 1000)
          : 0;
        responseStartTimeRef.current = null;
        console.log('[OpenAIRealtime] Response completed, duration:', durationSeconds, 's');

        // Save usage data for billing/tracking
        if (event.response?.usage) {
          saveVoiceUsage(event.response, durationSeconds);
        }
        
        // Check if there are any remaining function calls to process
        const output = event.response?.output || [];
        const hasIncompleteFunction = output.some(
          (item) => item.type === 'function_call' && item.status !== 'completed'
        );
        if (!hasIncompleteFunction) {
          setStatus('listening');
        }
        break;

      case 'response.cancelled':
        // Response was cancelled
        responseInProgressRef.current = false;
        currentResponseIdRef.current = null;
        console.log('[OpenAIRealtime] Response cancelled');
        break;

      case 'error':
        console.error('[OpenAIRealtime] Server error:', event.error);
        setError(event.error?.message || 'Server error');
        // Reset response state on error
        responseInProgressRef.current = false;
        currentResponseIdRef.current = null;
        break;

      default:
        break;
    }
  }, [saveVoiceUsage]);

  // Helper function to get voice feedback message for tool calls
  const getToolFeedbackMessage = (toolName) => {
    const messages = {
      list_tasks: "Checking your tasks...",
      create_task: "Creating your task...",
      update_task: "Updating your task...",
      delete_task: "Deleting the task...",
      complete_task: "Marking task as complete...",
      list_notes: "Looking at your notes...",
      create_note: "Creating your note...",
      list_groceries: "Checking your grocery list...",
      add_grocery: "Adding to grocery list...",
      delete_grocery: "Removing from grocery list...",
      mark_grocery_purchased: "Marking as purchased...",
      list_reminders: "Checking your reminders...",
      create_reminder: "Setting your reminder...",
      google_calendar_list_events: "Checking your calendar...",
      google_calendar_create_event: "Creating calendar event...",
      gmail_list_emails: "Checking your emails...",
      gmail_send_email: "Sending your email...",
      search_web: "Searching the web...",
      navigate_app: "Opening that for you...",
      manage_memory: "Checking my memory...",
      list_meetings: "Looking at your meetings...",
      search_meetings: "Searching your meetings...",
    };
    return messages[toolName] || null; // Return null for tools that don't need feedback
  };

  const handleFunctionCall = async (event) => {
    const { call_id, name, arguments: argsString } = event;

    console.log(`[OpenAIRealtime] Function call: ${name}`, argsString);
    
    // Check retry count - stop after 2 failed attempts
    const failKey = `${name}_${argsString}`;
    const failCount = failedCallsRef.current.get(failKey) || 0;
    if (failCount >= 2) {
      console.warn(`[OpenAIRealtime] Tool ${name} failed ${failCount} times, stopping retries`);
      if (dataChannelRef.current?.readyState === 'open') {
        dataChannelRef.current.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id,
            output: JSON.stringify({ 
              result: `I was unable to complete this action after multiple attempts. Please try a different command or check if the item exists.`
            }),
          },
        }));
        createResponse();
      }
      failedCallsRef.current.delete(failKey);
      return;
    }

    // Detect platform (mobile vs web)
    const isMobile = window.innerWidth <= 768 || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const platform = isMobile ? 'mobile' : 'web';

    // Set timeout for progress feedback if taking too long
    const progressTimeout = setTimeout(() => {
      if (dataChannelRef.current?.readyState === 'open') {
        console.log(`[OpenAIRealtime] Tool ${name} taking long, sending progress message`);
        dataChannelRef.current.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'assistant',
            content: [{ type: 'text', text: 'Still working on it, one moment...' }]
          }
        }));
      }
    }, 4000); // After 4 seconds

    try {
      // Execute function via backend
      const response = await fetch(`${BACKEND_URL}/api/openai-realtime/function-call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenRef.current}`,
        },
        body: JSON.stringify({
          functionCalls: [{
            call_id,
            name,
            arguments: argsString,
          }],
          platform, // Pass platform to backend
        }),
      });

      clearTimeout(progressTimeout);

      if (!response.ok) {
        throw new Error(`Function call failed: ${response.status}`);
      }

      const data = await response.json();
      const result = data.functionResponses?.[0];

      console.log(`[OpenAIRealtime] Function result:`, result);

      // Check if result contains an error - track for retry limiting
      let parsedOutput;
      try {
        parsedOutput = JSON.parse(result?.output || '{}');
      } catch { 
        parsedOutput = {}; 
      }
      
      if (parsedOutput.error) {
        // Increment fail count for this specific call
        failedCallsRef.current.set(failKey, failCount + 1);
        console.log(`[OpenAIRealtime] Tool ${name} returned error, fail count: ${failCount + 1}`);
      } else {
        // Success - reset fail count
        failedCallsRef.current.delete(failKey);
      }

      // Send result back via data channel
      if (dataChannelRef.current?.readyState === 'open') {
        // Mark response as complete since we're about to request a new one
        responseInProgressRef.current = false;
        currentResponseIdRef.current = null;
        
        // Create function call output
        dataChannelRef.current.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id,
            output: result?.output || JSON.stringify({ error: 'No result' }),
          },
        }));

        // Request new response (safe, since we just marked the previous as complete)
        createResponse();
      }
    } catch (err) {
      clearTimeout(progressTimeout);
      console.error('[OpenAIRealtime] Function call error:', err);
      
      // Track error for retry limiting
      failedCallsRef.current.set(failKey, failCount + 1);
      
      if (dataChannelRef.current?.readyState === 'open') {
        // Mark response as complete since we're about to request a new one
        responseInProgressRef.current = false;
        currentResponseIdRef.current = null;
        
        dataChannelRef.current.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id,
            output: JSON.stringify({ error: err.message }),
          },
        }));
        
        // Request new response
        createResponse();
      }
    }
  };

  const disconnect = useCallback(async () => {
    console.log('[OpenAIRealtime] Disconnecting...');

    // End voice chat session for history
    if (sessionIdRef.current && tokenRef.current && sessionStartTimeRef.current) {
      const durationSeconds = Math.floor((new Date() - sessionStartTimeRef.current) / 1000);
      try {
        await fetch(`${BACKEND_URL}/api/voice-chat/${sessionIdRef.current}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokenRef.current}`,
          },
          body: JSON.stringify({
            ended_at: new Date().toISOString(),
            duration_seconds: durationSeconds,
          }),
        });
        console.log('[OpenAIRealtime] Voice chat session ended, duration:', durationSeconds, 's');
      } catch (err) {
        console.warn('[OpenAIRealtime] Failed to end session:', err);
      }
      sessionIdRef.current = null;
      sessionStartTimeRef.current = null;
    }

    // Stop video capture interval
    if (captureIntervalRef.current) {
      clearInterval(captureIntervalRef.current);
      captureIntervalRef.current = null;
    }

    // Stop audio analysis
    if (audioAnimationRef.current) {
      cancelAnimationFrame(audioAnimationRef.current);
      audioAnimationRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
      analyserRef.current = null;
    }
    setAudioLevel(0);
    setActiveFeatures([]);

    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach((track) => track.stop());
      videoStreamRef.current = null;
    }

    if (audioElementRef.current) {
      audioElementRef.current.srcObject = null;
      audioElementRef.current = null;
    }

    setStatus('idle');
    setTranscript('');
    setError(null);
    setIsVideoEnabled(false);
    setIsScreenSharing(false);
    setIsMicMuted(false);
    // Notify parent that voice session has ended
    onVoiceEnd?.();
  }, [onVoiceEnd]);

  // Capture and send a frame to the model
  const captureAndSendFrame = useCallback(() => {
    if (!videoStreamRef.current) {
      console.error('[OpenAIRealtime] No video stream');
      setError('No video stream available');
      return;
    }
    if (!dataChannelRef.current) {
      console.error('[OpenAIRealtime] No data channel');
      setError('Not connected to voice chat');
      return;
    }
    if (dataChannelRef.current.readyState !== 'open') {
      console.error('[OpenAIRealtime] Data channel not open:', dataChannelRef.current.readyState);
      setError('Connection not ready');
      return;
    }

    const video = videoElementRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      console.error('[OpenAIRealtime] Video or canvas element not found');
      setError('Video element not ready');
      return;
    }

    const ctx = canvas.getContext('2d');
    // Use smaller dimensions to reduce data size for WebRTC
    canvas.width = 512;
    canvas.height = 384;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to base64 JPEG with lower quality for smaller size
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.5);
    const imageSizeKB = Math.round((imageDataUrl.length * 0.75) / 1024);
    console.log(`[OpenAIRealtime] Frame captured: ${canvas.width}x${canvas.height}, ~${imageSizeKB}KB`);
    
    // Send image to the model via data channel
    // Format per OpenAI docs: data:image/jpeg;base64,{base64_data}
    const event = {
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [
          {
            type: 'input_image',
            image_url: imageDataUrl,
          },
        ],
      },
    };

    try {
      console.log('[OpenAIRealtime] Sending frame to model...');
      dataChannelRef.current.send(JSON.stringify(event));
      console.log('[OpenAIRealtime] Frame sent successfully');
    } catch (err) {
      console.error('[OpenAIRealtime] Failed to send frame:', err);
      setError(`Failed to send frame: ${err.message}`);
    }
  }, []);

  // Start automatic frame capture
  const startAutoCapture = useCallback(() => {
    if (captureIntervalRef.current) {
      clearInterval(captureIntervalRef.current);
    }
    
    console.log('[OpenAIRealtime] Starting auto-capture (every 2 seconds)');
    // Capture frame every 2 seconds
    captureIntervalRef.current = setInterval(() => {
      if (videoStreamRef.current && dataChannelRef.current?.readyState === 'open') {
        captureAndSendFrame();
      }
    }, 2000);
  }, [captureAndSendFrame]);

  // Stop automatic frame capture
  const stopAutoCapture = useCallback(() => {
    if (captureIntervalRef.current) {
      console.log('[OpenAIRealtime] Stopping auto-capture');
      clearInterval(captureIntervalRef.current);
      captureIntervalRef.current = null;
    }
  }, []);

  // Handle auto-capture toggle while video is active
  useEffect(() => {
    if (isVideoEnabled && dataChannelRef.current?.readyState === 'open') {
      if (isAutoCapture) {
        startAutoCapture();
      } else {
        stopAutoCapture();
      }
    }
  }, [isAutoCapture, isVideoEnabled, startAutoCapture, stopAutoCapture]);

  // Callback ref to attach video stream when video element is mounted
  const videoRefCallback = useCallback((node) => {
    videoElementRef.current = node;
    if (node && videoStreamRef.current) {
      console.log('[OpenAIRealtime] Video element mounted, attaching stream');
      node.srcObject = videoStreamRef.current;
      node.play().catch(err => {
        console.warn('[OpenAIRealtime] Video play error:', err);
      });
    }
  }, []);

  // Start camera video
  const startCamera = useCallback(async () => {
    try {
      console.log('[OpenAIRealtime] Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480 },
      });
      videoStreamRef.current = stream;

      // Set state first - video element will be rendered, then useEffect will attach stream
      setIsVideoEnabled(true);
      setIsScreenSharing(false);
      setError(null);
      console.log('[OpenAIRealtime] Camera started successfully');

      // Start auto-capture if enabled
      if (isAutoCapture) {
        startAutoCapture();
      }
    } catch (err) {
      console.error('[OpenAIRealtime] Camera error:', err);
      setError(`Camera error: ${err.message || err.name}`);
    }
  }, [isAutoCapture, startAutoCapture]);

  // Stop video/screen share (defined before startScreenShare since it's used there)
  const stopVideo = useCallback(() => {
    if (captureIntervalRef.current) {
      clearInterval(captureIntervalRef.current);
      captureIntervalRef.current = null;
    }
    
    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach((track) => track.stop());
      videoStreamRef.current = null;
    }
    
    if (videoElementRef.current) {
      videoElementRef.current.srcObject = null;
    }
    
    setIsVideoEnabled(false);
    setIsScreenSharing(false);
    console.log('[OpenAIRealtime] Video stopped');
  }, []);

  // Toggle microphone mute
  const toggleMicMute = useCallback(() => {
    if (mediaStreamRef.current) {
      const audioTrack = mediaStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMicMuted(!audioTrack.enabled);
        console.log('[OpenAIRealtime] Microphone', audioTrack.enabled ? 'unmuted' : 'muted');
      }
    }
  }, []);

  // Start screen sharing
  const startScreenShare = useCallback(async () => {
    try {
      console.log('[OpenAIRealtime] Requesting screen share...');
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { width: 1280, height: 720 },
      });
      videoStreamRef.current = stream;

      // Handle when user stops sharing via browser UI
      stream.getVideoTracks()[0].onended = () => {
        stopVideo();
      };

      // Set state first - video element will be rendered, then useEffect will attach stream
      setIsVideoEnabled(true);
      setIsScreenSharing(true);
      setError(null);
      console.log('[OpenAIRealtime] Screen sharing started successfully');

      // Start auto-capture if enabled
      if (isAutoCapture) {
        startAutoCapture();
      }
    } catch (err) {
      console.error('[OpenAIRealtime] Screen share error:', err);
      setError('Failed to share screen');
    }
  }, [isAutoCapture, startAutoCapture, stopVideo]);

  // Send current frame manually (for "what do you see?" type questions)
  const sendCurrentFrame = useCallback(() => {
    if (!isVideoEnabled) {
      setError('Enable camera or screen share first');
      return;
    }
    captureAndSendFrame();
    
    // Trigger a response (safely handles in-progress responses)
    createResponse();
  }, [isVideoEnabled, captureAndSendFrame, createResponse]);

  // Expose control methods to parent component via ref
  // MUST be after all function definitions (connect, startCamera, startScreenShare, disconnect)
  useImperativeHandle(ref, () => ({
    // Start audio-only voice session
    startAudio: async () => {
      if (status === 'idle' || status === 'error') {
        await connect();
      }
    },
    // Start voice with webcam
    startWebcam: async () => {
      if (status === 'idle' || status === 'error') {
        await connect();
        // Start camera after connection is established
        setTimeout(() => startCamera(), 500);
      } else if (status === 'listening' || status === 'speaking') {
        await startCamera();
      }
    },
    // Start voice with screen share (renamed to avoid conflict with internal function)
    startScreen: async () => {
      if (status === 'idle' || status === 'error') {
        await connect();
        // Start screen share after connection is established
        setTimeout(() => startScreenShare(), 500);
      } else if (status === 'listening' || status === 'speaking') {
        await startScreenShare();
      }
    },
    // Stop voice session
    stop: () => disconnect(),
    // Stop only video/screen share (keep voice active)
    stopVideoOnly: () => stopVideo(),
    // Check if voice is active
    isActive: () => status !== 'idle' && status !== 'error',
    // Check if video is active
    isVideoActive: () => isVideoEnabled,
    // Check if screen sharing
    isScreenShare: () => isScreenSharing,
    // Get current status
    getStatus: () => status,
  }), [status, connect, startCamera, startScreenShare, disconnect, stopVideo, isVideoEnabled, isScreenSharing]);

  const getStatusColor = () => {
    switch (status) {
      case 'listening': return '#4CAF50';
      case 'speaking': return '#2196F3';
      case 'thinking': return '#FF9800';
      case 'connecting': return '#9C27B0';
      case 'error': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'listening': return '🎤';
      case 'speaking': return '🔊';
      case 'thinking': return '🤔';
      case 'connecting': return '🔄';
      case 'error': return '❌';
      default: return '🎙️';
    }
  };

  // If showUI is false, only render minimal hidden elements needed for functionality
  if (!showUI) {
    return (
      <>
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        {isVideoEnabled && (
          <video ref={videoRefCallback} autoPlay playsInline muted style={{ display: 'none' }} />
        )}
      </>
    );
  }

  return (
    <motion.div
      className="voice-chat-enhanced"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      {/* Content - Always visible, no dropdown header */}
      <div className="voice-chat-content">
            {/* Hidden canvas for video frame capture */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {/* Video preview */}
            <AnimatePresence>
              {isVideoEnabled && (
                <motion.div
                  className="voice-video-preview"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <video
                    ref={videoRefCallback}
                    autoPlay
                    muted
                    playsInline
                    style={{
                      width: '100%',
                      maxHeight: '180px',
                      borderRadius: '8px',
                      backgroundColor: '#000',
                      objectFit: 'cover',
                    }}
                  />
                  <div className="voice-video-label">
                    {isScreenSharing ? '🖥️ Screen Share' : '📷 Camera'}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Main Orb with Progress Ring */}
            <div className="voice-orb-wrapper">
              <ProgressRing
                size={130}
                strokeWidth={3}
                isConnecting={status === 'connecting'}
                isProcessing={status === 'thinking'}
              />
              <VoiceOrb
                state={isMicMuted ? 'muted' : (status === 'thinking' ? 'processing' : status)}
                audioLevel={audioLevel}
                onClick={status === 'idle' || status === 'error' ? connect : toggleMicMute}
                disabled={status === 'connecting'}
              />
            </div>

            {/* Status Text */}
            <motion.div
              className={`voice-status-text ${isMicMuted ? 'muted' : status}`}
              key={isMicMuted ? 'muted' : status}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              {isMicMuted ? 'Muted - Tap orb to unmute' :
               status === 'idle' ? 'Tap to Start' :
               status === 'connecting' ? 'Connecting...' :
               status === 'listening' ? 'Listening...' :
               status === 'speaking' ? 'Zoomey is speaking...' :
               status === 'thinking' ? 'Thinking...' :
               status === 'error' ? 'Connection error' :
               status.charAt(0).toUpperCase() + status.slice(1)}
            </motion.div>

            {/* Control Buttons */}
            <div className="voice-controls">
              {status === 'idle' || status === 'error' ? (
                <motion.button
                  className="voice-btn voice-btn-start"
                  onClick={connect}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Start
                </motion.button>
              ) : (
                <motion.button
                  className="voice-btn voice-btn-end"
                  onClick={disconnect}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  End Call
                </motion.button>
              )}
            </div>

            {/* Media controls - only show when connected */}
            <AnimatePresence>
              {status !== 'idle' && status !== 'error' && status !== 'connecting' && (
                <motion.div
                  className="voice-video-controls"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  {/* Mute button - always visible when connected */}
                  <motion.button
                    className={`voice-media-btn ${isMicMuted ? 'muted' : ''}`}
                    onClick={toggleMicMute}
                    title={isMicMuted ? 'Unmute Microphone' : 'Mute Microphone'}
                    whileHover={{ scale: 1.03, y: -1 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <i className={`bi ${isMicMuted ? 'bi-mic-mute-fill' : 'bi-mic-fill'}`}></i>
                    <span>{isMicMuted ? 'Unmute' : 'Mute'}</span>
                  </motion.button>

                  {/* Camera/Screen buttons when no video active, Stop button when video active */}
                  {!isVideoEnabled ? (
                    <>
                      <motion.button
                        className="voice-media-btn"
                        onClick={startCamera}
                        title="Start Camera"
                        whileHover={{ scale: 1.03, y: -1 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        <i className="bi bi-camera-video-fill"></i>
                        <span>Camera</span>
                      </motion.button>
                      <motion.button
                        className="voice-media-btn"
                        onClick={startScreenShare}
                        title="Share Screen"
                        whileHover={{ scale: 1.03, y: -1 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        <i className="bi bi-display"></i>
                        <span>Screen</span>
                      </motion.button>
                    </>
                  ) : (
                    <motion.button
                      className="voice-media-btn stop-media"
                      onClick={stopVideo}
                      title={isScreenSharing ? 'Stop Screen Share' : 'Stop Camera'}
                      whileHover={{ scale: 1.03, y: -1 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <i className={`bi ${isScreenSharing ? 'bi-display' : 'bi-camera-video-off-fill'}`}></i>
                      <span>{isScreenSharing ? 'Stop Share' : 'Stop Video'}</span>
                    </motion.button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error Display */}
            <AnimatePresence>
              {error && (
                <motion.div
                  className="voice-error"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <span className="voice-error-icon">⚠️</span>
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Live Conversation History - Shows when voice chat is active and showTranscript is true */}
            {showTranscript && (
              <AnimatePresence>
                {conversationHistory.length > 0 && (
                  <motion.div
                    className="voice-conversation-container"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <div className="voice-conversation-header">
                      <span>Live Conversation</span>
                      <span className="voice-conversation-count">{conversationHistory.length} messages</span>
                    </div>
                    <div className="voice-conversation-list">
                      {conversationHistory.map((msg, index) => (
                        <motion.div
                          key={index}
                          className={`voice-message ${msg.role}`}
                          initial={{ opacity: 0, x: msg.role === 'user' ? 20 : -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="voice-message-avatar">
                            {msg.role === 'user' ? '👤' : '🤖'}
                          </div>
                          <div className="voice-message-content">
                            <span className="voice-message-role">
                              {msg.role === 'user' ? 'You' : 'Zoomey'}
                            </span>
                            <span className="voice-message-text">{msg.content}</span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}

      </div>
    </motion.div>
  );
});

export default OpenAIRealtimeTest;
